import { addMinutes, format } from "date-fns";
import { DateTimeWithTimeZone } from "../entities/date_time_with_time_zone";
import { PaginationOptions } from "../entities/pagination_options";
import { Period } from "../entities/period";
import { Resource, TreeResources } from "../entities/resource";
import { ErrorHandler, Settings } from "../settings";
import { logHttpie } from "../utils/log";
import { ApiClientError } from "./response/ApiClientError";
import { ResponseWithStatus } from "./response/response_with_status";

/**
 * A list of paths that will be handled by the new CORE backend
 */
const CORE_PATH_URLS: readonly (string | RegExp)[] = [
  // /v{:digit}+/ ie. /v1/divisions
  /^\/v(\d)+\//
];

function isCoreUrl(pathname: string) {
  return (
    CORE_PATH_URLS.findIndex((x) =>
      typeof x === "string" ? pathname.startsWith(x) : x.test(pathname)
    ) > -1
  );
}

/**
 * Maps organization slugs to the slug that should be sent in the X-Tactic-Orgz header.
 * Used when an org operates under a different backend slug than its frontend slug.
 */
const HEADER_SLUG_OVERRIDES: Record<string, string> = {
  "redbull-new": "redbull",
  "hpe-new": "hpe"
};

export function getHeaderSlug(slug: string): string {
  return HEADER_SLUG_OVERRIDES[slug] ?? slug;
}

const isTacticApiProxyEnabled = () => {
  return typeof window !== "undefined" && "__tacticApiProxy__" in window;
};

abstract class BaseEndpoints {
  public readonly id: string = "base";
  protected static readonly MAXIMUM_LIMIT: number = 2000;
  private static DATE_FORMAT = "yyyy-MM-dd";
  private static TIME_FORMAT = "HH:mm:ss";
  protected readonly settings: Settings;
  private readonly organizationSlug: string | null;
  private readonly isIntegrations: boolean;
  private pendingRequests: Record<
    string,
    { resolve: any; reject: () => void }
  > = {};

  constructor(
    settings: Settings,
    organizationSlug: string | null,
    isIntegrations = false,
    id = ""
  ) {
    this.settings = settings;
    this.organizationSlug = organizationSlug;
    this.isIntegrations = isIntegrations;
    this.id = id;
    if (settings.legacyAuth && !settings.urlCore) {
      throw new Error("If legacyAuth is used, urlCore should be defined");
    }
  }

  protected getSlug(): string | null {
    return this.organizationSlug;
  }

  protected getBaseUrl(): string {
    return this.settings.url;
  }

  protected getAccessToken(): string | null {
    return this.settings?.legacyAuth?.access ?? null;
  }

  protected createPeriod(
    start: string,
    duration: number,
    time_zone: string
  ): Period {
    const startDate = new Date(start);
    const endDate = addMinutes(startDate, duration);
    const period: Period = {
      start: this.createDateTimeWithTimeZone(startDate),
      end: this.createDateTimeWithTimeZone(endDate),
      time_zone: time_zone
    };
    return period;
  }

  private createDateTimeWithTimeZone(date: Date): DateTimeWithTimeZone {
    return `${format(date, BaseEndpoints.DATE_FORMAT)}T${format(date, BaseEndpoints.TIME_FORMAT)}.000Z`;
  }

  public formatGenericParams(
    params: { [idx: string]: string | string[] | number | boolean | null } = {}
  ) {
    const urlParams = new URLSearchParams();
    for (const [key, val] of Object.entries(params)) {
      if (Array.isArray(val)) {
        val.forEach((x) => {
          urlParams.append(key, x.toString());
        });
      } else if (typeof val === "boolean" && val) {
        urlParams.append(key, "");
      } else if (val) {
        urlParams.set(key, val.toString());
      }
    }
    return urlParams;
  }

  public addGenericParamsToUrl(
    url: string,
    params: { [idx: string]: string | string[] | number | boolean } = {}
  ) {
    const formatted = this.formatGenericParams(params);
    const urlParams = formatted.toString();
    if (urlParams.toString() !== "") {
      return `${url}?${urlParams}`;
    }
    return url;
  }

  public async requestWithStatus<T>(
    method: string,
    url: string,
    body: any | null = null,
    customSettings: Partial<Settings> = {}
  ): Promise<ResponseWithStatus<T>> {
    const { legacyAuth, ...rest } = customSettings;
    const settings: Settings = { ...this.settings, ...rest };
    if (legacyAuth) {
      settings.legacyAuth = {
        ...settings.legacyAuth,
        ...legacyAuth
      };
    }

    const headers = this.buildRequestHeaders(settings);

    const request: {
      headers: Record<string, string>;
      method: string;
      credentials?: RequestCredentials;
      body?: any;
    } = {
      headers: headers,
      method: method,
      credentials: "include"
    };

    if (body !== null) {
      request.body = typeof body !== "string" ? JSON.stringify(body) : body;
    }

    const endpointUrl = this.buildUrl(url, settings);
    const log = settings.debug && (method !== "GET" || settings.debugGet);

    let logHeaders: Record<string, string> = {};
    if (settings.debugAuth) {
      logHeaders = request.headers;
    }

    if (log) {
      if (settings.debugOutput === "httpie") {
        logHttpie(endpointUrl, request, logHeaders);
      } else {
        // biome-ignore lint/suspicious/noConsoleLog: Log with httpie mode
        console.log(`${request.method} ${endpointUrl} request:`, {
          headers: logHeaders,
          body: typeof request.body !== "undefined" ? request.body : null
        });
      }
    }

    let response: Response;
    let responseWithStatus: ResponseWithStatus<T>;
    try {
      response = await fetch(endpointUrl, request);

      if (settings.getCookiesToSet) {
        responseWithStatus = ResponseWithStatus.CreateGetCookiesToSet(response);
      } else {
        responseWithStatus = ResponseWithStatus.Create(
          response.status,
          await response.text()
        );
      }

      if (!response.ok && !settings.notThrowWithInvalidResponses) {
        if (
          settings.onError !== undefined &&
          settings.onError !== null &&
          (settings.onError as ErrorHandler)
        ) {
          settings.onError(
            response.statusText,
            response.status,
            responseWithStatus
          );
        }
        const error = new ApiClientError(response.statusText);
        error.response = responseWithStatus;
        throw error;
      }
    } catch (error) {
      if (log) {
        console.error(`${request.method} ${endpointUrl} error:`, error);
      }

      if (
        // we don't want to send the error to the handler again
        !(error instanceof ApiClientError) &&
        settings.onError !== undefined &&
        settings.onError !== null &&
        (settings.onError as ErrorHandler)
      ) {
        // If not a network error, there will be no HTTP status
        settings.onError(error);
      }
      throw error;
    }
    return responseWithStatus;
  }

  private buildRequestHeaders(settings: Settings): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": settings?.contentType || "application/json"
    };

    if (this.organizationSlug !== null) {
      headers["X-Tactic-Orgz"] = getHeaderSlug(this.organizationSlug);
    }

    if (settings.corsBypass) {
      headers["X-Tactic-Cors-Bypass"] = settings.corsBypass;
    }

    if (settings.invitationCode) {
      headers["X-Tactic-Invitation-Code"] = settings.invitationCode;
    }

    if (settings.publicVisitsCode) {
      headers["X-Tactic-Public-Visits-Code"] = settings.publicVisitsCode;
    }

    if (settings.ticketShareCode) {
      headers["X-Tactic-Ticket-Share-Code"] = settings.ticketShareCode;
    }

    if (settings.stationId) {
      headers["X-Tactic-Station-Id"] = settings.stationId;
    }

    if (settings.legacyAuth) {
      headers.Authorization = `Bearer ${settings.legacyAuth.access}`;
      if (settings.legacyAuth.impersonation !== null) {
        headers["X-Tactic-Impersonation"] = settings.legacyAuth.impersonation;
      }
    }

    if (settings.adminAuth) {
      if (settings.adminAuth.organizationId) {
        headers["X-Tactic-Organization-Id"] = settings.adminAuth.organizationId;
      }

      if (settings.adminAuth.userEmail) {
        headers["X-Tactic-User-Email"] = settings.adminAuth.userEmail;
      }

      if (settings.adminAuth.userId) {
        headers["X-Tactic-User-Id"] = settings.adminAuth.userId;
      }
    }

    if (
      settings.authHeader ||
      (settings.requestsToUnion && settings.unionToken)
    ) {
      headers.Authorization = settings.authHeader || settings.unionToken || "";
    }

    // Se non c'è già un Authorization header e siamo in un ambiente browser,
    // controlla localStorage per union_auth
    if (!headers.Authorization && typeof window !== "undefined") {
      try {
        const unionAuth = window.localStorage.getItem("union_auth");
        if (unionAuth) {
          headers.Authorization = unionAuth;
        }
      } catch {
        // Ignora errori di accesso a localStorage (es. in contesti con restrizioni)
      }
    }

    return headers;
  }

  // requestStream issues a request with the same auth/URL as requestWithStatus
  // but returns the raw Response so the caller can consume a streaming body
  // (e.g. Server-Sent Events).
  protected async requestStream(
    method: string,
    url: string,
    body: unknown = null
  ): Promise<Response> {
    const settings = this.settings;
    const request: RequestInit = {
      headers: this.buildRequestHeaders(settings),
      method,
      credentials: "include"
    };
    if (body !== null) {
      request.body = typeof body !== "string" ? JSON.stringify(body) : body;
    }
    return fetch(this.buildUrl(url, settings), request);
  }

  protected async request(
    method: string,
    url: string,
    body: any = null,
    withStatus?: boolean
  ): Promise<any> {
    const response: ResponseWithStatus<any> = await this.requestWithStatus(
      method,
      url,
      body
    );
    return withStatus
      ? { status: response.status, json: response.result }
      : response.result;
  }

  private buildUrl(url: string, settings: Settings) {
    const requestUrl = settings.url;
    if (settings.requestsToUnion) {
      return `${settings.urlUnion}${url.startsWith("/v1") ? `/api${url}` : url}`;
    }
    if (settings.legacyAuth || settings.adminAuth) {
      const base = isCoreUrl(url) ? settings.urlCore : requestUrl;
      return `${base}${url}`;
    }
    if (this.isIntegrations) {
      return `${requestUrl}/api/integrations${url}`;
    }
    return `${requestUrl}/api${url}`;
  }
}

export class BasePublicEndpoints extends BaseEndpoints {
  constructor(settings: Settings) {
    super(settings, null, undefined, "public");
  }
}

export class BaseUserEndpoints extends BaseEndpoints {
  constructor(settings: Settings) {
    super(settings, null, undefined, "users");
  }
}

export class BaseSettingsEndpoints extends BaseEndpoints {
  constructor(settings: Settings, organizationSlug: string | null) {
    super(settings, organizationSlug, undefined, "settings");
  }

  protected get<T>(url: string): Promise<ResponseWithStatus<T>> {
    return this.requestWithStatus<T>("GET", url);
  }

  protected post<P, R>(url: string, body: P): Promise<ResponseWithStatus<R>> {
    return this.requestWithStatus<R>("POST", url, body);
  }

  protected update<P, R>(url: string, body: P): Promise<ResponseWithStatus<R>> {
    return this.requestWithStatus<R>("PUT", url, body);
  }

  protected delete<T>(url: string): Promise<ResponseWithStatus<T>> {
    return this.requestWithStatus<T>("DELETE", url);
  }
}

export class BaseOrganizationEndpoints extends BaseEndpoints {
  constructor(settings: Settings, organizationSlug: string | null) {
    super(settings, organizationSlug, undefined, "organizations");
  }

  protected formatTree(
    tree: TreeResources[],
    formatter: (
      node: TreeResources,
      formattedChildren: Resource[] | null
    ) => Resource
  ): Resource[] {
    const elements = [];

    for (const node of tree) {
      const children: Resource[] = [];
      // We go just one level deep for now, so copy pasting code basically instead of recursion
      if (node.children.length > 0) {
        for (const innerNode of node.children) {
          if (innerNode.children.length === 0) {
            children.push(formatter(innerNode, null));
          }
        }
      }
      if (children.length > 0) {
        elements.push(formatter(node, children));
      } else {
        elements.push(formatter(node, null));
      }
    }

    return elements;
  }

  protected getUrl(url: string): string {
    const organizationSlug = this.getSlug();
    let domain = this.settings.domain;
    if (domain.startsWith("https://")) {
      domain = this.settings.domain.slice(8, domain.length);
    }
    return `${this.settings.protocol}://${organizationSlug}.${domain}/${url}`;
  }

  protected paginatedEndpointUrl(
    url: string,
    pagination: PaginationOptions | null
  ): string {
    if (!pagination) {
      return url;
    }
    return (
      url +
      (url.indexOf("?") >= 0 ? "&" : "?") +
      "limit=" +
      (pagination.limit
        ? pagination.limit
        : BaseOrganizationEndpoints.MAXIMUM_LIMIT) +
      (pagination.cursor !== null
        ? `&cursor=${encodeURIComponent(pagination.cursor)}`
        : "")
    );
  }
}

export class BaseIntegrationEndpoints extends BaseEndpoints {
  constructor(settings: Settings, organizationSlug: string | null) {
    super(settings, organizationSlug, true, "integrations");
  }
}

export class BaseExternalsEndpoints extends BaseEndpoints {
  constructor(settings: Settings, organizationSlug: string | null) {
    super(settings, organizationSlug, false, "externals");
  }
}
